import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";

interface PolicyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: "privacy" | "terms";
}

const PolicyModal = ({ open, onOpenChange, type }: PolicyModalProps) => {
  const isPrivacy = type === "privacy";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] bg-background text-foreground">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            {isPrivacy ? "Política de Privacidade" : "Termos de Uso"}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            TH Coquetelaria - Última atualização: Janeiro de 2025
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="h-[60vh] pr-4">
          {isPrivacy ? <PrivacyContent /> : <TermsContent />}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

const PrivacyContent = () => (
  <div className="space-y-6 text-sm text-muted-foreground">
    <section>
      <h3 className="text-base font-semibold text-foreground mb-2">1. Introdução</h3>
      <p>
        A TH Coquetelaria ("nós", "nosso" ou "empresa") está comprometida em proteger sua privacidade. 
        Esta Política de Privacidade explica como coletamos, usamos, compartilhamos e protegemos suas 
        informações pessoais quando você utiliza nosso site e serviços.
      </p>
    </section>

    <section>
      <h3 className="text-base font-semibold text-foreground mb-2">2. Informações que Coletamos</h3>
      <p className="mb-2">Podemos coletar as seguintes informações:</p>
      <ul className="list-disc list-inside space-y-1 ml-2">
        <li>Nome completo</li>
        <li>Endereço de e-mail</li>
        <li>Número de telefone</li>
        <li>Informações sobre o evento (data, local, número de convidados)</li>
        <li>Dados de navegação e cookies</li>
      </ul>
    </section>

    <section>
      <h3 className="text-base font-semibold text-foreground mb-2">3. Como Usamos suas Informações</h3>
      <p className="mb-2">Utilizamos suas informações para:</p>
      <ul className="list-disc list-inside space-y-1 ml-2">
        <li>Responder às suas solicitações e consultas</li>
        <li>Elaborar propostas personalizadas para seus eventos</li>
        <li>Enviar comunicações sobre nossos serviços</li>
        <li>Melhorar nosso site e serviços</li>
        <li>Cumprir obrigações legais</li>
      </ul>
    </section>

    <section>
      <h3 className="text-base font-semibold text-foreground mb-2">4. Compartilhamento de Dados</h3>
      <p>
        Não vendemos, alugamos ou compartilhamos suas informações pessoais com terceiros para fins 
        de marketing. Podemos compartilhar dados apenas com prestadores de serviços que nos auxiliam 
        na operação do negócio, sempre sob acordos de confidencialidade.
      </p>
    </section>

    <section>
      <h3 className="text-base font-semibold text-foreground mb-2">5. Segurança dos Dados</h3>
      <p>
        Implementamos medidas de segurança técnicas e organizacionais para proteger suas informações 
        contra acesso não autorizado, alteração, divulgação ou destruição. No entanto, nenhum método 
        de transmissão pela Internet é 100% seguro.
      </p>
    </section>

    <section>
      <h3 className="text-base font-semibold text-foreground mb-2">6. Seus Direitos (LGPD)</h3>
      <p className="mb-2">De acordo com a Lei Geral de Proteção de Dados (LGPD), você tem direito a:</p>
      <ul className="list-disc list-inside space-y-1 ml-2">
        <li>Confirmar a existência de tratamento de dados</li>
        <li>Acessar seus dados pessoais</li>
        <li>Corrigir dados incompletos ou desatualizados</li>
        <li>Solicitar a exclusão de dados desnecessários</li>
        <li>Revogar o consentimento a qualquer momento</li>
      </ul>
    </section>

    <section>
      <h3 className="text-base font-semibold text-foreground mb-2">7. Cookies</h3>
      <p>
        Utilizamos cookies para melhorar sua experiência de navegação. Você pode configurar seu 
        navegador para recusar cookies, mas isso pode afetar algumas funcionalidades do site.
      </p>
    </section>

    <section>
      <h3 className="text-base font-semibold text-foreground mb-2">8. Contato</h3>
      <p>
        Para exercer seus direitos ou esclarecer dúvidas sobre esta política, entre em contato 
        através do e-mail: comercial@thcoquetelaria.com.br
      </p>
    </section>
  </div>
);

const TermsContent = () => (
  <div className="space-y-6 text-sm text-muted-foreground">
    <section>
      <h3 className="text-base font-semibold text-foreground mb-2">1. Aceitação dos Termos</h3>
      <p>
        Ao acessar e utilizar o site da TH Coquetelaria, você concorda com estes Termos de Uso. 
        Se você não concordar com qualquer parte destes termos, não utilize nosso site ou serviços.
      </p>
    </section>

    <section>
      <h3 className="text-base font-semibold text-foreground mb-2">2. Descrição dos Serviços</h3>
      <p>
        A TH Coquetelaria oferece serviços de coquetelaria para eventos, incluindo bartenders 
        profissionais, drinks autorais, estrutura completa de bar e bebidas originais. Os serviços 
        são prestados mediante contrato específico para cada evento.
      </p>
    </section>

    <section>
      <h3 className="text-base font-semibold text-foreground mb-2">3. Solicitação de Propostas</h3>
      <p className="mb-2">
        Ao solicitar uma proposta através do nosso site:
      </p>
      <ul className="list-disc list-inside space-y-1 ml-2">
        <li>Você declara que as informações fornecidas são verdadeiras</li>
        <li>A proposta enviada é válida por 30 dias</li>
        <li>A contratação só é efetivada mediante assinatura de contrato</li>
        <li>Valores podem variar conforme especificidades do evento</li>
      </ul>
    </section>

    <section>
      <h3 className="text-base font-semibold text-foreground mb-2">4. Propriedade Intelectual</h3>
      <p>
        Todo o conteúdo do site, incluindo textos, imagens, logotipos, fotografias e design, 
        são de propriedade exclusiva da TH Coquetelaria e estão protegidos pelas leis de 
        propriedade intelectual. É proibida a reprodução sem autorização prévia.
      </p>
    </section>

    <section>
      <h3 className="text-base font-semibold text-foreground mb-2">5. Uso do Site</h3>
      <p className="mb-2">Você concorda em não:</p>
      <ul className="list-disc list-inside space-y-1 ml-2">
        <li>Utilizar o site para fins ilegais ou não autorizados</li>
        <li>Tentar acessar áreas restritas do site</li>
        <li>Interferir no funcionamento do site</li>
        <li>Coletar informações de outros usuários</li>
      </ul>
    </section>

    <section>
      <h3 className="text-base font-semibold text-foreground mb-2">6. Limitação de Responsabilidade</h3>
      <p>
        A TH Coquetelaria não se responsabiliza por danos indiretos, incidentais ou consequentes 
        resultantes do uso ou impossibilidade de uso do site. O site é fornecido "como está", 
        sem garantias de qualquer tipo.
      </p>
    </section>

    <section>
      <h3 className="text-base font-semibold text-foreground mb-2">7. Links Externos</h3>
      <p>
        Nosso site pode conter links para sites de terceiros. Não nos responsabilizamos pelo 
        conteúdo, políticas de privacidade ou práticas desses sites externos.
      </p>
    </section>

    <section>
      <h3 className="text-base font-semibold text-foreground mb-2">8. Modificações</h3>
      <p>
        Reservamo-nos o direito de modificar estes Termos de Uso a qualquer momento. 
        As alterações entram em vigor imediatamente após sua publicação no site.
      </p>
    </section>

    <section>
      <h3 className="text-base font-semibold text-foreground mb-2">9. Legislação Aplicável</h3>
      <p>
        Estes Termos de Uso são regidos pelas leis brasileiras. Qualquer disputa será resolvida 
        no foro da comarca de Ribeirão Preto - SP.
      </p>
    </section>

    <section>
      <h3 className="text-base font-semibold text-foreground mb-2">10. Contato</h3>
      <p>
        Para dúvidas sobre estes Termos de Uso, entre em contato através do 
        e-mail: comercial@thcoquetelaria.com.br
      </p>
    </section>
  </div>
);

export default PolicyModal;
